<nav class="sui-navbar sui-navbar-2col">
  <!-- ── Start zone (sidebar toggle + breadcrumbs) ──────────── -->
  <div class="sui-navbar-start">
    <button
      class="sui-btn sui-btn-ghost sui-btn-sm sui-btn-icon"
      [attr.aria-pressed]="sidebarOpen()"
      (click)="toggleSidenav()"
      [suiTooltip]="'Toggle sidebar (' + ('mod' | platformKey) + ' \\)'"
      suiTooltipPlacement="bottom"
    >
      <i class="sui-icon sui-icon-sidebar-simple" [class.text-primary-500]="sidebarOpen()"></i>
    </button>
    @if (envBadge; as env) {
      <span
        class="sui-badge sui-badge-sm sui-badge-round font-medium"
        [class.sui-badge-neutral]="env.color === 'neutral'"
        [class.sui-badge-info]="env.color === 'info'"
        [class.sui-badge-warning]="env.color === 'warning'"
        [class.sui-badge-secondary]="env.color === 'secondary'"
        [suiTooltip]="env.tooltip"
        suiTooltipPlacement="bottom"
      >
        {{ env.label }}
      </span>
    }
    @if (hasBreadcrumbs()) {
      <sui-breadcrumbs
        class="hidden md:flex"
        [items]="breadcrumbItems()"
        separator="chevron"
        size="sm"
        (itemClick)="onBreadcrumbClick($event)"
      />
    }
  </div>

  <!-- ── End zone (spatial groups: discovery | tools | identity) -->
  <div class="sui-navbar-end">
    <!-- ── Discovery group ──────────────────────────────────── -->
    <div class="sui-navbar-group">
      <button
        class="sui-btn sui-btn-ghost sui-btn-sm gap-2"
        aria-label="Open command palette"
        data-guide-target="navbar-command-palette"
        (click)="openCommandPalette()"
      >
        <i class="sui-icon sui-icon-magnifying-glass text-muted"></i>
        <span class="text-muted font-normal hidden sm:inline">Search or jump to...</span>
        <span class="hidden md:flex items-center gap-1 ml-auto">
          <kbd class="sui-kbd sui-kbd-sm">{{ 'mod' | platformKey }}</kbd>
          <kbd class="sui-kbd sui-kbd-sm">K</kbd>
        </span>
      </button>
    </div>

    <!-- ── Tools group ──────────────────────────────────────── -->
    <!-- The guide target sits on the group, not on an X-Ray variant: three render at different
         breakpoints and only one is ever in the DOM. -->
    <div class="sui-navbar-group" data-guide-target="navbar-xray">
      <!-- X-Ray (inline ≥xl, dropdown md–xl, hidden <md) -->
      <ng-container *hasAsset>
        <ng-container *permissions="[Permissions.READ_XRAY]">
          <!-- Inline buttons — xl and up -->
          <div class="hidden xl:flex items-center gap-2 flex-nowrap">
            <span class="text-xs font-medium text-muted whitespace-nowrap">X-Ray</span>
            <button
              class="sui-btn sui-btn-ghost sui-btn-sm sui-btn-icon sui-btn-primary"
              [attr.aria-pressed]="xrayReflow()"
              (click)="toggleXray(XRayOptions.Reflow)"
              suiTooltip="Reflow complex paragraphs for readability"
              suiTooltipPlacement="bottom"
            >
              <i class="sui-icon sui-icon-text-indent"></i>
            </button>
            <button
              class="sui-btn sui-btn-ghost sui-btn-sm sui-btn-icon sui-btn-primary"
              [attr.aria-pressed]="xrayLegalLogic()"
              (click)="toggleXray(XRayOptions.LegalLogic)"
              suiTooltip="Highlight key legal logic"
              suiTooltipPlacement="bottom"
            >
              <i class="sui-icon sui-icon-git-branch"></i>
            </button>
            <button
              class="sui-btn sui-btn-ghost sui-btn-sm sui-btn-icon sui-btn-primary"
              [attr.aria-pressed]="xrayEntities()"
              (click)="toggleXray(XRayOptions.Entities)"
              suiTooltip="Highlight amounts, dates, and entities"
              suiTooltipPlacement="bottom"
            >
              <i class="sui-icon sui-icon-highlighter-circle"></i>
            </button>
          </div>

          <!-- Dropdown button — md to xl -->
          <div class="hidden md:flex xl:hidden items-center">
            <button
              #xrayTrigger
              class="sui-btn sui-btn-ghost sui-btn-sm gap-1.5"
              [class.sui-btn-primary]="hasActiveXray()"
              (click)="xrayFilter.toggle()"
            >
              <i class="sui-icon sui-icon-eye"></i>
              <span class="text-xs font-medium">X-Ray</span>
            </button>
            <sui-filter-dropdown
              #xrayFilter
              [definition]="xrayFilterDef"
              [(value)]="xrayFilterValue"
              [showTrigger]="false"
              [triggerEl]="xrayTrigger"
              size="sm"
              (closed)="onXrayFilterApply()"
            />
          </div>
        </ng-container>
      </ng-container>

      <!-- Manager Approval -->
      <ng-container *hasAsset>
        <!-- Separates this cluster from X-Ray; only where X-Ray actually renders (md and up) -->
        <div class="sui-navbar-divider hidden md:block" *permissions="[Permissions.READ_XRAY]"></div>
        <semeris-manager-approval-dropdown />
      </ng-container>

      <!-- Notes -->
      <ng-container *hasAsset>
        <ng-container *permissions="[Permissions.READ_COMMENT]">
          <button
            class="sui-btn sui-btn-sm sui-btn-icon"
            [class.sui-btn-ghost]="!notesPanelOpen()"
            [class.sui-btn-primary]="notesPanelOpen()"
            [attr.aria-pressed]="notesPanelOpen()"
            (click)="onToggleNotesPanel()"
            data-guide-target="navbar-notes"
            suiTooltip="Notes"
            suiTooltipPlacement="bottom"
          >
            <i class="sui-icon sui-icon-chat-circle"></i>
          </button>
        </ng-container>
      </ng-container>

      <!-- Help — verified users only; the full User Guide page stays open to everyone.
           Shown on `/help/**` too: the page has no Product Updates or Guided Tours, so the panel
           is the only way to reach them from there. -->
      @if (verifiedUser()) {
        <semeris-help-icon data-guide-target="navbar-help" />
      }
    </div>

    <!-- ── Interface group (classic ⇄ next) ─────────────────── -->
    @if (showDeploymentToggle) {
      <div class="sui-navbar-group hidden lg:flex" *permissions="[Permissions.READ_NEXT_UI]">
        <div class="sui-swap sui-swap-sm sui-swap-primary" role="group" aria-label="Interface">
          @for (option of deploymentVariants; track option.variant) {
            <button
              class="sui-badge sui-swap-item"
              [attr.aria-pressed]="deploymentVariant === option.variant"
              [suiTooltip]="option.tooltip"
              suiTooltipPlacement="bottom"
              (click)="onSwitchDeployment(option.variant)"
            >
              {{ option.label }}
            </button>
          }
        </div>
      </div>
    }

    <!-- ── Identity group ───────────────────────────────────── -->
    <div class="sui-navbar-group">
      <!-- Status badges (conditional) -->
      @if (isImpersonating()) {
        <span class="sui-badge sui-badge-warning sui-badge-round text-xs hidden lg:inline-flex">
          <i class="sui-icon sui-icon-warning"></i>
          @if (isReadOnlyImpersonation()) {
            <b>Read-only</b> &middot;
          }
          Viewed as
          @if (impersonatedUser(); as user) {
            <b>{{ user | userNameResolver$ | async }}</b>
          }
          @if (impersonatedUser() && impersonatedTenant()) {
            in
          }
          @if (impersonatedTenant(); as tenant) {
            <b>{{ tenant | tenantNameResolver$ | async }}</b>
          }
          <button class="sui-badge-close" (click)="onExitImpersonation()" aria-label="Exit impersonation">
            <i class="sui-icon sui-icon-x"></i>
          </button>
        </span>
      }
      @if (isMultiTenant() && !isImpersonating() && activeTenant(); as tenant) {
        <semeris-tenant-switcher [activeTenant]="tenant" [tenants]="switchableTenants()" (switchTenant)="onSwitchTenant($event)" />
      }
      @if (onboardingTenant()) {
        <span class="sui-badge sui-badge-round text-xs hidden lg:inline-flex">
          <i class="sui-icon sui-icon-gear"></i>
          Setting up account
        </span>
      }

      <!-- Avatar dropdown -->
      @if (userInfo(); as userInfo) {
        <button
          class="sui-btn sui-btn-ghost sui-btn-sm gap-2"
          [suiDropdown]="avatarMenu"
          suiDropdownAlign="end"
          [attr.aria-label]="userInfo.name"
        >
          <sui-avatar [src]="userInfo.picture" [name]="userInfo.name" size="sm">
            <span class="sui-dot" [class]="connectionDot().dotClass" [suiTooltip]="connectionDot().tooltip"></span>
            @if (isImpersonating() || onboardingTenant()) {
              <span
                class="sui-dot sui-avatar-dot-top lg:hidden"
                [class.sui-dot-warning]="isImpersonating()"
                [class.sui-dot-primary]="!isImpersonating() && onboardingTenant()"
              ></span>
            }
          </sui-avatar>
          <span class="text-sm text-surface-content hidden xl:inline truncate max-w-32">{{ userInfo.name }}</span>
          <i class="sui-icon sui-icon-caret-down text-muted"></i>
        </button>

        <ng-template #avatarMenu>
          <div class="w-56 p-3 space-y-3">
            <!-- User info -->
            <div class="flex items-center gap-2">
              <sui-avatar [src]="userInfo.picture" [name]="userInfo.name" size="md" />
              <div class="min-w-0">
                <div class="text-sm font-medium text-surface-content truncate">{{ userInfo.name }}</div>
                <div class="text-xs text-muted truncate">{{ userInfo.email }}</div>
              </div>
            </div>

            <!-- Status section (shown in dropdown at <lg when badges are hidden) -->
            @if (isImpersonating()) {
              <div class="lg:hidden border-t border-border -mx-3 px-3 pt-2">
                <span class="sui-badge sui-badge-warning sui-badge-round text-xs">
                  <i class="sui-icon sui-icon-warning"></i>
                  @if (isReadOnlyImpersonation()) {
                    <b>Read-only</b> &middot;
                  }
                  Viewed as
                  @if (impersonatedUser(); as user) {
                    <b>{{ user | userNameResolver$ | async }}</b>
                  }
                  @if (impersonatedUser() && impersonatedTenant()) {
                    in
                  }
                  @if (impersonatedTenant(); as tenant) {
                    <b>{{ tenant | tenantNameResolver$ | async }}</b>
                  }
                  <button class="sui-badge-close" (click)="onExitImpersonation()" aria-label="Exit impersonation">
                    <i class="sui-icon sui-icon-x"></i>
                  </button>
                </span>
              </div>
            }

            <!-- Interface switch (shown in dropdown at <lg when the toggle is hidden) -->
            @if (otherDeploymentVariant; as other) {
              <div class="lg:hidden border-t border-border -mx-3 px-3 pt-2" *permissions="[Permissions.READ_NEXT_UI]">
                <button class="sui-btn sui-btn-sm sui-btn-secondary w-full" (click)="onSwitchDeployment(other.variant)">
                  Switch to {{ other.label }}
                </button>
              </div>
            }

            <!-- Settings -->
            <ul class="sui-menu sui-menu-sm">
              <li class="sui-menu-title">Account</li>
              <li>
                <button role="menuitem" [routerLink]="['settings', 'profile'] | wsLink">
                  <i class="sui-icon sui-icon-user-circle-gear"></i>
                  Profile Settings
                </button>
              </li>
              @if (!onboardingTenant()) {
                <li>
                  <button role="menuitem" [routerLink]="['setup'] | wsLink">
                    <i class="sui-icon sui-icon-sparkle"></i>
                    Workspace setup
                  </button>
                </li>
              }
              <ng-container *permissions="[Permissions.READ_MY_TENANT]">
                <li>
                  <button role="menuitem" [routerLink]="['settings', 'tenant'] | wsLink">
                    <i class="sui-icon sui-icon-identification-badge"></i>
                    Tenant Settings
                  </button>
                </li>
              </ng-container>
            </ul>

            <!-- Theme -->
            <ul class="sui-menu sui-menu-sm" data-sui-no-close>
              <li class="sui-menu-title">Theme</li>
              <li class="px-2">
                <semeris-theme-picker [mode]="themeMode()" (modeChange)="setTheme($event)" [disabled]="isReadOnlyImpersonation()" size="xs" />
              </li>
            </ul>

            <!-- Help -->
            @if (showHelpMenu()) {
              <ul class="sui-menu sui-menu-sm">
                <li class="sui-menu-title">Help</li>
                @if (verifiedUser()) {
                  <li>
                    <button role="menuitem" [routerLink]="userGuideLink">
                      <i class="sui-icon sui-icon-question"></i>
                      User Guide
                    </button>
                  </li>
                }
                @if (canRunGuidedTours()) {
                  <li>
                    <button role="menuitem" (click)="onOpenGuidedTours()">
                      <i class="sui-icon sui-icon-graduation-cap"></i>
                      Guided Tours
                    </button>
                  </li>
                }
              </ul>
            }

            <!-- Footer -->
            <div class="border-t border-border -mx-3 -mb-3 px-3 py-2 mt-1">
              <ul class="sui-menu sui-menu-sm">
                <li>
                  <button role="menuitem" (click)="onFeedback()">
                    <i class="sui-icon sui-icon-paper-plane-tilt"></i>
                    Send Feedback
                  </button>
                </li>
                <li>
                  <a role="menuitem" href="https://semeris.com" target="_blank" rel="noopener noreferrer">
                    <i class="sui-icon sui-icon-arrow-square-out"></i>
                    Visit semeris.com
                  </a>
                </li>
                <li>
                  <button role="menuitem" class="sui-menu-item-error" (click)="logout()">
                    <i class="sui-icon sui-icon-sign-out"></i>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </ng-template>
      }
    </div>
  </div>
</nav>

<!-- ── Command palette: custom result template ───────────── -->
<ng-template #resultTpl let-item let-active="active">
  @switch (itemData(item)?.resultType) {
    <!-- Deal result: dot + title + subtitle + library badge -->
    @case ('deal') {
      @if (itemData(item); as d) {
        <span
          class="sui-dot"
          [class.sui-dot-success]="$any(d).qualityTrafficLight === 'Green'"
          [class.sui-dot-neutral]="$any(d).qualityTrafficLight === 'Grey' || $any(d).qualityTrafficLight === 'Gray'"
        ></span>
        <div class="sui-cmdpal-item-content">
          <span class="sui-cmdpal-item-label">
            {{ item.label }}
            @if ($any(d).isPrivate) {
              <i class="sui-icon sui-icon-lock text-muted text-xs ml-1"></i>
            }
            @if ($any(d).autoPublished) {
              <i class="sui-icon sui-icon-lightning text-muted text-xs ml-1"></i>
            }
          </span>
          <span class="sui-cmdpal-item-desc">{{ item.description }}</span>
        </div>
        @if ($any(d).hasLatestIssuance) {
          <i class="sui-icon sui-icon-seal-warning text-error-300 text-sm" suiTooltip="A newer issuance of this deal exists"></i>
        }
        @if ($any(d).endOfLife) {
          <span class="sui-badge sui-badge-warning sui-badge-sm">End of life</span>
        }
        @if ($any(d).libraryDisplayName) {
          <span class="sui-badge sui-badge-accent sui-badge-sm sui-badge-outline">{{ $any(d).libraryDisplayName }}</span>
        }
        @let inBasket = isInBasket($any(d).transactionId);
        <button
          type="button"
          class="sui-btn sui-btn-ghost sui-btn-icon sui-btn-xs"
          [suiTooltip]="inBasket ? 'Remove from basket' : 'Add to basket'"
          stopPropagation
          (click)="onToggleBasket($any(d).transactionId)"
        >
          <i class="sui-icon sui-icon-basket" [class.sui-icon-fill]="inBasket" [class.text-primary-500]="inBasket"></i>
        </button>
      }
    }

    <!-- Datapoint result: icon + title + type/refId row -->
    @case ('datapoint') {
      @if (itemData(item); as d) {
        <i class="sui-icon sui-icon-database"></i>
        <div class="sui-cmdpal-item-content">
          <span class="sui-cmdpal-item-label">{{ item.label }}</span>
          @if (item.description) {
            <span class="sui-cmdpal-item-desc">{{ item.description }}</span>
          }
          <span class="sui-cmdpal-item-desc">
            @if ($any(d).displayType) {
              <span class="uppercase">{{ $any(d).displayType }}</span>
            }
            @if ($any(d).refId) {
              @if ($any(d).displayType) {
                ·
              }
              {{ $any(d).refId }}
            }
            @if ($any(d).displayShortName) {
              · <span class="uppercase">{{ $any(d).displayShortName }}</span>
            }
          </span>
        </div>
      }
    }

    <!-- Topic result: icon + title + refId -->
    @case ('topic') {
      @if (itemData(item); as d) {
        <i class="sui-icon sui-icon-rows"></i>
        <div class="sui-cmdpal-item-content">
          <span class="sui-cmdpal-item-label">{{ item.label }}</span>
          @if (item.description) {
            <span class="sui-cmdpal-item-desc">{{ item.description }}</span>
          }
          @if ($any(d).refId || $any(d).displayShortName) {
            <span class="sui-cmdpal-item-desc">
              {{ $any(d).refId }}
              @if ($any(d).displayShortName) {
                · <span class="uppercase">{{ $any(d).displayShortName }}</span>
              }
            </span>
          }
        </div>
        @if ($any(d).builtIn) {
          <span class="sui-badge sui-badge-sm sui-badge-outline shrink-0">
            <i class="sui-icon sui-icon-seal-check"></i>
            <span>Built-in</span>
          </span>
        }
      }
    }

    <!-- Default: standard icon + label + description + toggle/shortcut -->
    @default {
      @if (item.icon) {
        <i class="sui-icon" [class]="'sui-icon-' + item.icon"></i>
      }
      <div class="sui-cmdpal-item-content">
        <span class="sui-cmdpal-item-label">{{ item.label }}</span>
        @if (item.description) {
          <span class="sui-cmdpal-item-desc">{{ item.description }}</span>
        }
      </div>
      @if (item.toggle !== undefined) {
        <i class="sui-icon sui-icon-check sui-cmdpal-item-toggle" [style.opacity]="item.toggle ? 1 : 0"></i>
      } @else if (item.shortcut?.length) {
        <div class="sui-cmdpal-item-shortcut">
          @for (key of item.shortcut; track key) {
            <kbd class="sui-kbd sui-kbd-sm">{{ key | platformKey }}</kbd>
          }
        </div>
      }
    }
  }
</ng-template>

<!-- ── Command palette: search bar actions ────────────────── -->
<ng-template #actionsTpl>
  @if (searchTool()) {
    <button
      class="sui-btn sui-btn-ghost sui-btn-sm sui-btn-icon"
      (click)="openFullTextSearch()"
      suiTooltip="Open full text search"
      suiTooltipPlacement="bottom"
    >
      <i class="sui-icon sui-icon-file-magnifying-glass"></i>
    </button>
  }
</ng-template>
